import Link from "next/link";
import classes from "./button.module.css";
import CustomSVG from "../svg";
import Dot from '@/assets/svg/_dot.svg';
import Loader from '@/assets/svg/icons/loader.svg';
import LinkWithParams from "@/components/link_with_params/link_with_params";

export default function CustomButton({
    children,
    href,
    size = 'lg',
    hierarchy = 'primary',
    icon,
    icon_type,
    is_svg,
    svg=false,
    destructive,
    active,
    query,
    rel,
    target,
    rememberSearchParams,
    linkClasses,
    isLoading = false,
    loadingText,
    wrapText = false,
    ...props
}) {
    const baseClassName = (props.className ? String(props.className) : '').trim();
    // Defensive defaults: many callers relied on implicit defaults historically.
    // If these become undefined, the rendered button loses padding/height styles.
    const normalizedSize = (typeof size === 'string' && size.trim()) ? size.trim() : 'lg';
    const normalizedHierarchy = (typeof hierarchy === 'string' && hierarchy.trim()) ? hierarchy.trim() : 'primary';

    // If a caller passes an unexpected `size` (or we introduce a new token later),
    // we still want a consistent layout. Fall back to `lg` if the CSS module key
    // doesn't exist.
    const sizeClass = classes[`size-${normalizedSize}`] || classes['size-lg'];
    const hierarchyClass = classes[normalizedHierarchy];
    const iconClass = icon ? classes[icon] : '';
    const destructiveClass = destructive ? classes.destructive : '';
    const activeClass = active ? classes.active : '';
    const isDisabled = Boolean(props.disabled || isLoading);

    // Build a clean className list. Never emit "undefined".
    props.className = [
        baseClassName,
        classes.button,
        sizeClass,
        hierarchyClass,
        destructiveClass,
        iconClass,
        activeClass,
    ].filter(Boolean).join(' ');
    props.disabled = isDisabled;
    if (isLoading) props['aria-busy'] = true;

    let icon_size = 20;
    switch(normalizedSize) {
        case "sm":
        case "md":
            props.className = `${props.className} text-sm-semibold`;
            icon_size = 20;
            break;
        case "2xl":
            props.className = `${props.className} text-lg-semibold`;
            icon_size = 24;
            break;
        case "lg":
        case "xl":
        default:
            props.className = `${props.className} text-md-semibold`;
            icon_size = 20;
            break;
    }

    let button_icon;
    if(icon) {
        if(icon === "dot") {
            button_icon = <CustomSVG SvgFunction={Dot} height={10} width={10} />
        } else if(is_svg) {
            button_icon = <CustomSVG SvgFunction={svg} height={icon_size} width={icon_size} />
        } else {
            button_icon = <i className={`bi bi-icon bi-icon-${icon_size} ${icon_type}`}/>
        }
    }

    const buttonChildren = isLoading && loadingText ? loadingText : children;
    const renderedButtonChildren = wrapText
        ? <span className={classes.wrappedText}>{buttonChildren}</span>
        : buttonChildren;
    const content = isLoading
        ? <>
            <CustomSVG SvgFunction={Loader} className={classes["loading-icon"]} height={icon_size} width={icon_size} />
            {renderedButtonChildren}
        </>
        : <>
            {icon && (icon === "leading" || icon === "only" || icon === "dot") && button_icon}
            {(!icon || (icon && icon !== "only")) && renderedButtonChildren}
            {icon && icon === "trailing" && button_icon}
        </>;

    let button = <button {...props}>{content}</button>;

    if(href) {
        const linkProps = {
            ...props,
            className: `${classes.link} ${linkClasses || ''} ${props.className}`.trim(),
            rel,
            target,
            query,
        };

        delete linkProps.disabled;

        if (isDisabled) {
            linkProps['aria-disabled'] = true;
            linkProps.tabIndex = -1;
            linkProps.onClick = (event) => {
                event.preventDefault();
                if (typeof props.onClick === 'function') props.onClick(event);
            };
        }

        if(rememberSearchParams) {
            button = <LinkWithParams href={href} {...linkProps}>
                {content}
            </LinkWithParams>
        } else {
            button = <Link href={href} {...linkProps}>
                {content}
            </Link>
        }
    }

    return button;
}